/**
 * 「管理者・講師を追加」の候補一覧 fetch（data 属性駆動。user-admin 05_ui.md §4.5）
 *
 * マークアップ規約:
 *   <div id="select-member" class="lms-overlay" data-lms-modal hidden>
 *     <div data-lms-remote-list="/api/admin/users?tab=member"
 *          data-lms-remote-list-empty="昇格できるユーザーがいません"></div>
 *   </div>
 *
 *   <div id="confirm-promote" class="lms-overlay" data-lms-modal hidden>
 *     <span data-lms-fill-slot="name"></span><span data-lms-fill-slot="email"></span>
 *     <button data-lms-confirm-ok>実行</button>
 *   </div>
 *   <form id="promote-form" method="post" th:action="@{/admin/users/0/role}" hidden></form>
 *
 * add-instructor-and-root-roles: 変更先ロールのラジオは #confirm-promote の中に置き、
 * form="promote-form" 属性で外部フォームに紐付ける（HTML5 の form 属性）。したがって
 * 本ファイルはロール選択を一切知らなくてよく、変更は action の書き換え先 URL だけで済む。
 *
 * モーダルが開かれたタイミング（openModal が発火する lms:modal:open）で候補一覧を取得する。
 * 選択された候補の情報は #confirm-promote の [data-lms-fill-slot] へ差し込み、#promote-form の
 * action を書き換えてから開く。
 * 開く直前に armConfirmModal(#confirm-promote, #promote-form) を呼び、OK ボタンを
 * confirm-submit.ts の結線機構に載せる（issue #4 / 05_ui.md §4.5 手順 6）。
 */
import { armConfirmModal, fillSlots } from "./confirm-submit.js";
import { closeModal, openModal } from "./modal.js";

const MSG_FETCH_FAILED = "ユーザーの取得に失敗しました";

interface CandidateUser {
  id: number;
  name: string;
  email: string;
}

function renderCandidates(
  container: HTMLElement,
  users: CandidateUser[],
  emptyMessage: string,
  onSelect: (user: CandidateUser) => void,
): void {
  container.innerHTML = "";
  if (users.length === 0) {
    const empty = document.createElement("div");
    empty.className = "lms-empty-card";
    empty.textContent = emptyMessage;
    container.appendChild(empty);
    return;
  }

  const list = document.createElement("div");
  list.className = "lms-menu";
  users.forEach((user) => {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "lms-menu__item";
    btn.dataset["userId"] = String(user.id);
    btn.dataset["userName"] = user.name;
    btn.dataset["userEmail"] = user.email;
    btn.textContent = `${user.name}\u3000${user.email}`;
    btn.addEventListener("click", () => onSelect(user));
    list.appendChild(btn);
  });
  container.appendChild(list);
}

export function initRemoteList(scope: ParentNode = document): void {
  scope.querySelectorAll<HTMLElement>("[data-lms-remote-list]").forEach((container) => {
    if (container.dataset["lmsRemoteListReady"] === "1") return;
    container.dataset["lmsRemoteListReady"] = "1";

    const url = container.dataset["lmsRemoteList"];
    const emptyMessage = container.dataset["lmsRemoteListEmpty"] ?? "";
    const modal = container.closest<HTMLElement>("[data-lms-modal]");
    if (!url || !modal) return;

    const confirmModal = document.getElementById("confirm-promote");
    const promoteForm = document.getElementById("promote-form") as HTMLFormElement | null;

    modal.addEventListener("lms:modal:open", () => {
      // 取得中であることを支援技術に伝える（部品 #15）。表示は従来どおり素のテキスト。
      container.setAttribute("aria-busy", "true");
      container.textContent = "読み込み中…";
      fetch(url, { credentials: "same-origin", headers: { Accept: "application/json" } })
        .then((response) => {
          if (!response.ok) {
            window.lmsToast(MSG_FETCH_FAILED, "error");
            container.textContent = "";
            return null;
          }
          return response.json() as Promise<{ users: CandidateUser[] }>;
        })
        .then((body) => {
          if (!body) return;
          renderCandidates(container, body.users, emptyMessage, (user) => {
            closeModal(modal);
            if (confirmModal) {
              fillSlots(confirmModal, { name: user.name, email: String(user.email) });
              if (promoteForm) {
                promoteForm.action = `/admin/users/${user.id}/role`;
                // 確認モーダルを開く直前に毎回結線する（issue #4 / design.md D1・D2）。
                // action 書き換えの「後」に呼ぶこと。pendingForms は modal id キーで
                // 上書きされるため、候補を選び直しても最後に選んだ候補の action を持つ
                // #promote-form が送信対象になる。
                armConfirmModal(confirmModal, promoteForm);
              }
              openModal(confirmModal);
            }
          });
        })
        .catch(() => {
          window.lmsToast(MSG_FETCH_FAILED, "error");
          container.textContent = "";
        })
        .finally(() => {
          // 成功・HTTP エラー・通信エラーのいずれの経路でも必ず外す（部品 #15）
          container.removeAttribute("aria-busy");
        });
    });
  });
}
